<template>
  <div class="blytz-date-picker" data-cy="blytz-date-picker">
    <div v-if="intervals.length > 0" class="intervals">
      <select
        v-model="selectedInterval"
        data-cy="interval-select"
        @change="onChangeInterval"
      >
        <option value="custom">Custom</option>
        <option
          v-for="interval in intervals"
          :key="interval.value"
          :value="interval.value"
          :selected="interval.default"
        >
          {{ interval.label }}
        </option>
      </select>
    </div>
    <DatePicker v-model="date" is-range :columns="columns" @input="updateDate">
      <template v-if="singleField" #default="{ inputValue, inputEvents }">
        <div class="date-range-fields">
          <input
            class="single"
            :value="`${inputValue.start} ${separator} ${inputValue.end}`"
            data-cy="date-range-single"
            @click="inputEvents.start.focusin"
            @focusout="inputEvents.start.focusout"
          />
        </div>
      </template>
      <template v-else #default="{ inputValue, inputEvents }">
        <div class="date-range-fields">
          <input
            :value="inputValue.start"
            data-cy="date-range-start"
            @click="inputEvents.start.focusin"
            @focusout="inputEvents.start.focusout"
          />
          {{ separator }}
          <input
            :value="inputValue.end"
            data-cy="date-range-end"
            @click="inputEvents.end.focusin"
            @focusout="inputEvents.end.focusout"
          />
        </div>
      </template>
    </DatePicker>
  </div>
</template>

<script lang="ts">
import { DatePicker } from 'v-calendar'
import { Vue } from 'vue-property-decorator'

interface Interval {
  label: string
  value: number
  default: boolean
}
export default Vue.extend({
  name: 'BlytzDatePicker',
  components: {
    DatePicker
  },
  props: {
    singleField: {
      type: Boolean,
      default: false
    },
    intervals: {
      type: Array as () => Interval[],
      default: () => []
    },
    columns: {
      type: Number,
      default: 2
    },
    separator: {
      type: String,
      default: 'to'
    },
    onDateChange: {
      type: Function,
      default: (date: { start: string; end: string }) => String
    },
    startDate: {
      type: String,
      default: new Date().toString()
    },
    endDate: {
      type: String,
      default: new Date().toString()
    }
  },
  data() {
    return {
      date: {
        start: new Date(this.startDate),
        end: new Date(this.endDate)
      },
      selectedInterval: `${
        this.intervals?.find(interval => interval.default)?.value
      }`
    }
  },
  methods: {
    updateDate(newDate: { start: Date; end: Date }) {
      this.date = newDate
      this.onDateChange(this.date)
      this.$emit('input', this.date)

      //check if the selected date range is present in the intervals, if so select it, otherwise will select "custom"
      const interval = this.intervals.find(
        interval =>
          new Date().getDate() - new Date(newDate.start).getDate() ===
          interval.value
      )
      this.selectedInterval = interval?.value ? `${interval.value}` : 'custom'
    },
    onChangeInterval(event) {
      const interval = this.intervals.find(
        interval => interval.value === Number(event.target.value)
      )

      if (interval) {
        const start = new Date()
        const end = new Date()
        start.setDate(start.getDate() - interval.value)
        this.date = { start, end }
        this.$emit('input', this.date)
      } else {
        event.target.value = 'custom'
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.blytz-date-picker {
  display: flex;
  gap: 0.8rem;
  select,
  input {
    height: 35px;
    padding: 7px;

    font-size: 0.9rem;
    text-align: center;

    border-radius: 7px;
  }

  select {
    height: 34px;
    margin: 1px 0 0;

    border-color: transparent;
    border-right: 10px solid transparent;
    box-shadow: 0 0 1px 1px #bfbfbf;
  }
  .date-range-fields {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    input {
      width: 110px;
      height: 35px;

      border: 1px solid #bfbfbf;
      &.single {
        width: 220px;
      }
    }
  }
}
</style>
